import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import Icons from "../icons";

/* ------------------------------------------------------------------ */
/*  Confirmation dialog                                                */
/*                                                                     */
/*  The one template for every "Are you sure" across CortexQA: a       */
/*  round theme-coloured icon, the title and the message, then an      */
/*  outlined No and a filled Yes on a light footer — all in the        */
/*  Cortex theme colour. Only the message differs per screen.          */
/* ------------------------------------------------------------------ */
export const UNDO_LINE = "This action cannot be undone.";
/* A line of the description: a "\n" in it starts a new line. */
const LINE = "whitespace-pre-line";
const UNDO_SENTENCE = /\s*This (?:action )?cannot be undone\.?/i;
const AND_UNDONE = /\s+and cannot be undone\./i;

/* The description: the message, then "This cannot be undone." on a line of
   its own — taken out of the message wherever a screen wrote it, or added
   with `irreversible`. */
export function describeConfirm(message, irreversible = false) {
  let body = message;
  let undo = Boolean(irreversible);
  if (typeof body === "string") {
    if (UNDO_SENTENCE.test(body)) {
      body = body.replace(UNDO_SENTENCE, "");
      undo = true;
    } else if (AND_UNDONE.test(body)) {
      body = body.replace(AND_UNDONE, ".");
      undo = true;
    }
    body = body.trim();
  }
  return { body, undo };
}

/* The question Yes and No answer, when the message asks none: a message
   with no "?" ("For you will stop listing your Jira work…") gets the title's
   question ("Disconnect Jira?") as its first line. A message that asks, a
   JSX one, or a title that isn't a question gets nothing added. */
export function questionFor(title, body) {
  if (typeof body !== "string" || body.includes("?")) return "";
  const asked = typeof title === "string" ? title.trim() : "";
  return asked.endsWith("?") ? asked : "";
}

/* The header always reads "Confirmation", and the message asks what is done
   (a "\n" in it starts a new line). The buttons always read No and Yes:
   `confirmLabel` is kept for callers and no longer shown. `title` stays the
   dialog's accessible name, and is shown only as the question of a message
   that asks none (questionFor). `tone` ("danger" /
   "primary") is kept for callers; both take the theme colour. `icon`
   replaces the template's "?". `option` ({label, checked, onChange, hint}) puts a
   checkbox on the footer's left, for a choice the action makes; `hint` is
   its hover text. */
export default function ConfirmModal({
  title,
  message,
  confirmLabel = "Yes", // eslint-disable-line no-unused-vars
  busyLabel = "Deleting…",
  onCancel,
  onConfirm,
  busy = false,
  tone = "danger", // eslint-disable-line no-unused-vars
  icon: BadgeIcon = null,
  size = "md",
  irreversible = false,
  option = null,
}) {
  /* "lg": wider and taller, for a message that lists several items. */
  const lg = size === "lg";
  const cancelRef = useRef(null);
  const optionId = useId();
  const { body, undo } = describeConfirm(message, irreversible);
  const question = questionFor(title, body);

  /* No has the focus first (the safe choice); Escape answers No. */
  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && !busy && onCancel?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-overlay/50 backdrop-blur-sm" onClick={busy ? undefined : onCancel} />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        className={`relative z-10 w-full overflow-hidden rounded-lg bg-card shadow-2xl max-w-2xl`}
      >
        {/* Body: the round icon, the title and the message. */}
        <div className={`flex gap-5 ${lg ? "min-h-[14rem] px-7 py-7" : "px-7 py-7"}`}>
          <span
            className="flex h-14 w-14 flex-none items-center justify-center rounded-full bg-brand-700 text-white"
            aria-hidden="true"
          >
            {BadgeIcon ? <BadgeIcon className="h-6 w-6" /> : <span className="text-xl font-medium leading-none">?</span>}
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-2xl font-medium tracking-tight text-slate-800">Confirmation</h3>
            {/* Each line of text keeps its "\n" breaks (LINE); a JSX message
                keeps its own layout. */}
            <div className="mt-2 space-y-1.5 text-base leading-relaxed text-slate-600">
              {question && <p className={LINE}>{question}</p>}
              {body && (typeof body === "string" ? <p className={LINE}>{body}</p> : <div>{body}</div>)}
              {undo && <p className={LINE}>{UNDO_LINE}</p>}
            </div>
          </div>
        </div>

        {/* Footer: outlined No, filled Yes, both in the theme colour. */}
        <div className="flex items-center justify-end gap-3 border-t border-slate-200 bg-brand-50/60 px-7 py-4">
          {/* Only the box ticks: the text beside it is not a <label>, so a
              click on it changes nothing. The box takes its name from the
              text (aria-labelledby, so a JSX label names it too) and its
              hint as its own hover, which screen readers say as well. */}
          {option && (
            <div title={option.hint || undefined} className="mr-auto flex items-center gap-2 text-[15px] text-slate-700">
              <input
                type="checkbox"
                aria-labelledby={optionId}
                title={option.hint || undefined}
                checked={Boolean(option.checked)}
                onChange={(e) => option.onChange?.(e.target.checked)}
                disabled={busy}
                className="h-5 w-5 flex-none cursor-pointer rounded border-slate-400 text-brand-700 focus:ring-brand-700 disabled:cursor-not-allowed"
              />
              <span id={optionId}>{option.label}</span>
            </div>
          )}
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="min-w-[8rem] rounded-md border-2 border-brand-700 bg-card px-6 py-2 text-base font-semibold text-brand-700 transition hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            No
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex min-w-[8rem] items-center justify-center gap-2 rounded-md bg-brand-700 px-6 py-2 text-base font-semibold text-white shadow-sm transition hover:bg-brand-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy && <Icons.Spinner className="h-4 w-4 animate-spin" />}
            {busy ? busyLabel : "Yes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* For a handler that asks before it acts, in place of the browser's
   confirm(): `const [ask, confirmDialog] = useConfirm();`, then
   `if (!(await ask({ title, message }))) return;`, and
   {confirmDialog} rendered by the screen. One question at a time. */
export function useConfirm() {
  const [pending, setPending] = useState(null);
  const ask = useCallback((opts) => new Promise((resolve) => setPending({ ...opts, resolve })), []);
  const settle = (ok) => {
    pending?.resolve(ok);
    setPending(null);
  };
  const dialog = pending ? (
    <ConfirmModal
      title={pending.title}
      message={pending.message}
      icon={pending.icon || null}
      irreversible={Boolean(pending.irreversible)}
      onCancel={() => settle(false)}
      onConfirm={() => settle(true)}
    />
  ) : null;
  return [ask, dialog];
}
